import React from "react";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { PanelShell, type PanelMeta } from "./PanelShell";

type PeersPanelProps = {
  panel: PanelMeta;
  headerColor: string;
  layoutEdit: boolean;
  reportMode?: boolean;
  peers: PeersResponse | null;
  peersLoading: boolean;
  peersError: string | null;
};

type PeerCompany = {
  Company_Number: string;
  Company_Name: string;
  Company_Status?: string | null;
  Incorporation_Date?: string | null;
  Age_Band?: string | null;
  Turnover_Year1?: number | null;
  Turnover_Band?: string | null;
  Profit_After_Tax_Year1?: number | null;
  Total_Assets_Year1?: number | null;
  Total_Debt_Year1?: number | null;
  Cash_Year1?: number | null;
  Employee_Band?: string | null;
  Industry?: string | null;
  Division?: string | null;
  County?: string | null;
  Post_Town?: string | null;
  Postcode?: string | null;
  Companies_House_Link?: string | null;
};

type PeersResponse = {
  count: number;
  results: PeerCompany[];
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

export function PeersPanel({
  panel,
  headerColor,
  layoutEdit,
  reportMode = false,
  peers,
  peersLoading,
  peersError,
}: PeersPanelProps) {
  const results = peers?.results ?? [];
  const peersToShow = reportMode ? results : results.slice(0, 8);
  const peerCardClass = reportMode
    ? "rounded-md border border-transparent bg-transparent p-2 report-avoid-break"
    : "rounded-md border border-border bg-card p-2";
  return (
    <PanelShell panel={panel} headerColor={headerColor} layoutEdit={layoutEdit} reportMode={reportMode}>
      <div className={`${reportMode ? "mt-2 gap-1.5" : "mt-3 gap-2"} flex flex-col text-[11px] text-muted-foreground`}>
        {peersError ? <div className="text-xs text-amber-600 dark:text-amber-400">{peersError}</div> : null}
        {peersLoading ? (
          <div className="text-xs text-muted-foreground italic">Loading peers…</div>
        ) : results.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No similar companies found.</div>
        ) : (
          <div className="grid gap-2">
            {peersToShow.map((peer) => (
              <div key={peer.Company_Number} className={peerCardClass}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-foreground">
                      {peer.Company_Name || "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {peer.Company_Number || "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {peer.Industry || "—"}
                      {peer.Division ? ` · ${peer.Division}` : ""}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {peer.Post_Town || peer.County || "—"}
                      {peer.Postcode ? ` · ${peer.Postcode}` : ""}
                    </div>
                  </div>
                  {!reportMode ? (
                    <Link
                      href={`/company/${peer.Company_Number}`}
                      className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                    >
                      View <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  ) : null}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</span>
                    <div>{peer.Company_Status || "—"}</div>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Incorporated</span>
                    <div>{formatDate(peer.Incorporation_Date)}</div>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Turnover</span>
                    <div>{peer.Turnover_Band || formatCurrency(peer.Turnover_Year1)}</div>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Employees</span>
                    <div>{peer.Employee_Band || "—"}</div>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Assets</span>
                    <div>{formatCurrency(peer.Total_Assets_Year1)}</div>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Profit</span>
                    <div>{formatCurrency(peer.Profit_After_Tax_Year1)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PanelShell>
  );
}
